import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { Input, Label } from "reactstrap";
import CopyButton from "../global/CopyButton";
import SpinnerIcon from "../global/SpinnerIcon";
import ApiStatusBadge from "./ApiStatusBadge";
import ApiKeyReveal from "../modals/ApiKeyReveal";
import ConfirmRotateApiKey, {
  GRACE_WINDOW_DAYS,
} from "../modals/ConfirmRotateApiKey";
import {
  fetchApiAccess,
  provisionApiAccess,
  rotateApiAccess,
  setApiCallLimit,
} from "../../store/actions/apiAccessAction";
import { getUsagePlan } from "../../utils/apiUsagePlans";
import CoursePicker from "./CoursePicker";
import UsagePanel from "./UsagePanel";

/*
    API Access (static#49) — D14 panel, D16 rotation, D17 call-limit toggle.
    Mounted from views/account/ApiManagement.js, which owns the page heading;
    everything below it is this component.

    SECURITY — the reason this component holds state at all:
    the Access Key ID and Secret Access Key returned by provision/rotate live in
    `revealed`, a LOCAL useState. They are never dispatched. src/store/index.js
    persists the `authUser` Redux slice to localStorage encrypted under the
    hardcoded AES key "my-secret-key", so anything that reaches the store is
    effectively on disk in the clear. `revealed` is cleared the moment the
    customer acknowledges the reveal modal.

    Do not add a console statement to the reveal path.
*/

const SUPPORT_EMAIL = "apisupport@golfbert.com";
const OVERAGE_RATE = "$0.005";

/*
    CALL LIMIT — HIDDEN FOR v1. Flip to true when the plans land.

    The toggle is "stop at my quota" vs "allow overage at $0.005/call", and API
    Gateway has no per-key switch for it: a key's quota comes entirely from its
    usage plan. So the toggle can only move a customer between a paired
    `-capped` and `-overage` plan for the same tier, and those pairs do not
    exist in the account — every live plan has a single quota.

    Showing a control that always fails is worse than not showing one, so it is
    hidden rather than left to answer 503 NOT_CONFIGURED at the customer.

    Nothing else is removed. PUT /api-access/call-limit, the setApiCallLimit
    thunk and this section are all correct and stay wired, so re-enabling is
    this one line. The blocker is business rules, not code.

    What has to happen first is captured on golfbert-api#34: decide whether
    "overage" means no quota or a high ceiling, create the paired plans, add
    config.resolvePairedUsagePlanId, and — the genuinely missing piece — meter
    overage into Stripe, because nothing meters anything today.
*/
const CALL_LIMIT_TOGGLE_ENABLED = false;

const formatDate = (value) =>
  value && moment(value).isValid()
    ? moment(value).format("MMMM D, YYYY")
    : null;

/**
 * For a customer whose plan this system cannot provision.
 *
 * They are usually PAYING — Starter and Custom: 5 Courses are both live in
 * Stripe with no rule behind them — so the tone is "we will sort this out",
 * not "your plan is invalid". Nothing they can do on this page will help, so
 * the page does not pretend otherwise: no picker, no button, one address.
 */
const PlanNeedsHelp = () => (
  <div className="gb-alert mb-3" role="status">
    We can't set up an API key for your plan automatically. For help with your
    API key contact us at{" "}
    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll get you
    sorted.
  </div>
);

const ApiAccess = ({ onUpdatePayment }) => {
  const dispatch = useDispatch();
  const { loading, saving, loaded, error, data } = useSelector(
    (state) => state.apiAccess
  );

  // LOCAL ONLY. Never dispatched. See the note at the top of this file.
  const [revealed, setRevealed] = useState(null);
  const [revealMode, setRevealMode] = useState("provision");
  const [confirmRotate, setConfirmRotate] = useState(false);
  // The picker's selection. Safe for local state — these are public course ids,
  // not credentials, and they are gone the moment the key is created.
  const [pickedCourses, setPickedCourses] = useState([]);

  useEffect(() => {
    dispatch(fetchApiAccess());
  }, [dispatch]);

  const status = data?.status || "none";
  /*
      Which courses this customer may choose, straight from the server, plus
      WHY it is absent when it is. `planStatus` is "ok", "none" (no API plan
      was ever bought) or "unsupported" (a real, often paying customer on a plan
      this system cannot provision — Starter and Custom: 5 Courses are both live
      today). Those last two need opposite things said to them, so the reason
      comes from the server rather than being guessed from a null.
  */
  const courseSelection = data?.courseSelection || null;
  const planStatus = data?.planStatus || (courseSelection ? "ok" : "none");
  const planUnsupported = planStatus === "unsupported";
  const noApiPlan = planStatus === "none";
  const planUsable = planStatus === "ok";
  const isWildcardPlan = courseSelection?.wildcard === true;
  // A plan may fix its courses rather than let the customer choose — the trial
  // covers course 4803 and nothing else. Offering a picker there invites a
  // choice that is discarded server-side.
  const fixedCourseIds = courseSelection?.fixedCourseIds || null;
  const hasFixedCourses =
    Array.isArray(fixedCourseIds) && fixedCourseIds.length > 0;
  const needsCourseSelection =
    planUsable && !isWildcardPlan && !hasFixedCourses;
  const courseIds = pickedCourses.map((course) => course.id);
  const cannotProvisionYet =
    !planUsable || (needsCourseSelection && courseIds.length === 0);
  const plan = getUsagePlan(data?.usagePlanId);
  const prevExpiresAt = data?.prevExpiresAt || null;
  const prevExpiresLabel = formatDate(prevExpiresAt);
  const rotationInProgress =
    !!prevExpiresAt &&
    moment(prevExpiresAt).isValid() &&
    moment(prevExpiresAt).isAfter(moment());

  const handleProvision = () => {
    dispatch(
      provisionApiAccess(courseIds, (credentials) => {
        setRevealMode("provision");
        setRevealed(credentials);
        // The selection has done its job and the entitlements are written.
        setPickedCourses([]);
      })
    );
  };

  const handleRotate = () => {
    setConfirmRotate(false);
    dispatch(
      rotateApiAccess((credentials) => {
        setRevealMode("rotate");
        setRevealed(credentials);
      })
    );
  };

  const handleAcknowledgeReveal = () => {
    // Drop the secret. This is the whole lifetime of that value in the app.
    setRevealed(null);
  };

  const handleToggleCallLimit = (e) => {
    dispatch(setApiCallLimit(e.target.checked));
  };

  if (loading && !loaded) {
    return (
      <div className="api-access">
        <SpinnerIcon /> <span className="ml-2">Loading…</span>
      </div>
    );
  }

  // A load failure must not blank the panel — say what happened and offer a
  // retry, because the customer's key is the thing they came here for.
  if (loaded && !data) {
    return (
      <div className="api-access">
        <p className="mb-3">
          {error || "We couldn't load your API access details."}
        </p>
        <button
          type="button"
          className="gb-btn gb-btn--secondary"
          onClick={() => dispatch(fetchApiAccess())}
        >
          Try again
        </button>
      </div>
    );
  }

  const keySection = (
    <div className="api-access__credential mb-4">
      {/*
          A plain <label>, not reactstrap's <Label>: reactstrap 9 stamps
          `form-label` on it, and custom.scss turns that into 11px uppercase —
          which shouted the header name and broke the code pill inside it.
      */}
      <label htmlFor="api-access-key" className="gb-label">
        Your API Key (<code className="gb-code">x-api-key</code> header)
      </label>
      <div className="d-flex align-items-center">
        <Input
          id="api-access-key"
          className="gb-field api-access__credential-input"
          type="text"
          value={data?.apigwKeyValue || ""}
          readOnly
          spellCheck={false}
          autoComplete="off"
          onFocus={(e) => e.target.select()}
        />
        <CopyButton
          value={data?.apigwKeyValue || ""}
          className="ml-2 flex-shrink-0"
          ariaLabel="Copy API key"
        />
      </div>
      <p className="gb-help">
        Your Secret Access Key is not shown here — it is displayed only once,
        when it is created. Rotate your key if you have lost it.
      </p>
    </div>
  );

  const planSection = (
    <div className="mb-4">
      <strong className="d-block">Current API plan</strong>
      {plan ? (
        <>
          <p className="mb-0">{plan.name}</p>
          <p className="mb-0 text-muted">{plan.quota}</p>
        </>
      ) : (
        <p className="mb-0 text-muted">
          {data?.usagePlanId
            ? `Plan ${data.usagePlanId}`
            : "No plan attached yet"}
        </p>
      )}
      <small className="text-muted font-italic">
        This is the limit enforced on api.golfbert.com.
      </small>
    </div>
  );

  const usageSection = <UsagePanel />;

  const callLimitSection = (
    <div className="mb-4">
      <strong className="d-block mb-2">Call limit</strong>
      <div className="api-access__toggle-row">
        <Input
          type="checkbox"
          id="api-access-call-limit"
          checked={!!data?.callLimitEnabled}
          onChange={handleToggleCallLimit}
          disabled={saving}
        />
        <Label for="api-access-call-limit" className="mb-0">
          Stop my calls at my plan limit
        </Label>
        {saving && <SpinnerIcon className="ml-2" />}
      </div>
      <small className="d-block text-muted mt-2">
        {data?.callLimitEnabled ? (
          <>
            <strong>On.</strong> Calls beyond your plan limit are rejected and
            you are never billed for overage. Turn this off to let extra calls
            through at {OVERAGE_RATE} per call.
          </>
        ) : (
          <>
            <strong>Off.</strong> Calls beyond your plan limit are allowed and
            billed at {OVERAGE_RATE} per call. Turn this on to have them
            rejected instead.
          </>
        )}
      </small>
    </div>
  );

  const rotationSection = (
    <div className="mb-2">
      <strong className="d-block mb-1">Rotate your key</strong>
      {rotationInProgress ? (
        <div className="gb-alert">
          A rotation is already in progress. Your previous key keeps working
          until <strong>{prevExpiresLabel}</strong>, then stops. You can rotate
          again after that date.
        </div>
      ) : (
        <>
          <p className="mb-2 text-muted">
            Creates a new key and secret. Your current key keeps working for{" "}
            {GRACE_WINDOW_DAYS} more days so you have time to deploy the change.
          </p>
          <button
            type="button"
            className="gb-btn gb-btn--danger"
            onClick={() => setConfirmRotate(true)}
            disabled={saving}
          >
            Rotate my API key
          </button>
        </>
      )}
      {data?.rotatedAt && (
        <small className="d-block text-muted mt-2">
          Last rotated {formatDate(data.rotatedAt)}.
        </small>
      )}
    </div>
  );

  let body = null;

  switch (status) {
    case "none":
      body = (
        <>
          <p className="mb-2">
            You don't have API credentials yet. They are created automatically
            when you subscribe to a paid API plan — if you have already
            subscribed, you can create them now.
          </p>
          {planUsable && isWildcardPlan && (
            <p className="text-muted small mb-2">
              Your plan covers every course, so there is nothing to choose.
            </p>
          )}
          {planUsable && hasFixedCourses && (
            <p className="text-muted small mb-2">
              Your trial covers course {fixedCourseIds.join(", ")}, so there is
              nothing to choose.
            </p>
          )}
          {noApiPlan && (
            <p className="text-muted small mb-2">
              You don't have an API plan yet. Choose one from the{" "}
              <Link to="/api/plans">plans and pricing</Link> page to get
              started.
            </p>
          )}
          {planUnsupported && <PlanNeedsHelp />}
          {needsCourseSelection && (
            <CoursePicker
              maxCourses={courseSelection?.maxCourses}
              selected={pickedCourses}
              onChange={setPickedCourses}
              disabled={saving}
            />
          )}
          <button
            type="button"
            className="gb-btn gb-btn--primary gb-btn--lg"
            onClick={handleProvision}
            disabled={saving || cannotProvisionYet}
          >
            {saving ? (
              <>
                <SpinnerIcon label="Creating your credentials" /> Creating
              </>
            ) : (
              "Create my API credentials"
            )}
          </button>
        </>
      );
      break;

    case "pending":
      body = (
        <>
          <p className="mb-2">
            We're setting up your API credentials. This usually takes a few
            seconds.
          </p>
          <button
            type="button"
            className="gb-btn gb-btn--secondary"
            onClick={() => dispatch(fetchApiAccess())}
            disabled={loading}
          >
            {loading ? (
              <>
                <SpinnerIcon /> Checking
              </>
            ) : (
              "Check again"
            )}
          </button>
        </>
      );
      break;

    case "active":
      body = (
        <>
          {keySection}
          {planSection}
          {usageSection}
          {CALL_LIMIT_TOGGLE_ENABLED && callLimitSection}
          {rotationSection}
        </>
      );
      break;

    case "suspended":
      // Reversible — do not use revocation language. Two causes reach this
      // state: a payment that failed (static#48 dunning) and, since
      // golfbert-api#31, a subscription that ended. This component cannot tell
      // them apart: GET /api-access sends `status: "suspended"` for both and no
      // reason, and Setting.js's subscription lookup only returns an `active`
      // subscription, so a failed-payment customer (past_due, unpaid or
      // cancelled by Stripe) looks the same there as one who cancelled. So the
      // copy names both causes and gives each its own way back.
      body = (
        <>
          <div className="gb-alert mb-4" role="alert">
            <strong>Your API access is paused.</strong> Your key is turned off
            because a payment didn't go through or your subscription ended.
            Nothing has been deleted. If a payment failed, update your payment
            method. If your subscription ended, choose a plan to subscribe
            again. Your same key starts working again once your payment goes
            through.
            <div className="mt-2">
              {onUpdatePayment && (
                <Link
                  onClick={onUpdatePayment}
                  className="link-to fw--500 text-nowrap mr-3"
                >
                  Update payment method
                </Link>
              )}
              <Link to="/api/plans" className="link-to fw--500 text-nowrap">
                See API plans
              </Link>
            </div>
          </div>
          {data?.apigwKeyValue && keySection}
          {planSection}
          {usageSection}
          {CALL_LIMIT_TOGGLE_ENABLED && callLimitSection}
        </>
      );
      break;

    case "revoked":
      body = (
        <>
          <div className="gb-alert mb-4" role="alert">
            <strong>Your API access has been revoked.</strong> Your key no
            longer works and can't be turned back on. For a new key, email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </div>
          <Link to="/api/plans" className="link-to fw--500">
            See API plans
          </Link>
        </>
      );
      break;

    case "failed":
      body = (
        <>
          <div className="gb-alert mb-4" role="alert">
            <strong>We couldn't finish setting up your API credentials.</strong>{" "}
            You have not been charged for API access you can't use. Try again,
            and if it fails a second time email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll
            sort it out.
          </div>
          {planUnsupported && <PlanNeedsHelp />}
          {needsCourseSelection && (
            <CoursePicker
              maxCourses={courseSelection?.maxCourses}
              selected={pickedCourses}
              onChange={setPickedCourses}
              disabled={saving}
            />
          )}
          <button
            type="button"
            className="gb-btn gb-btn--primary gb-btn--lg"
            onClick={handleProvision}
            disabled={saving || cannotProvisionYet}
          >
            {saving ? (
              <>
                <SpinnerIcon /> Creating
              </>
            ) : (
              "Try again"
            )}
          </button>
        </>
      );
      break;

    default:
      // An unrecognised status must not blank the panel.
      body = (
        <>
          <p className="mb-2">
            We couldn't determine the state of your API access. Please email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
          <button
            type="button"
            className="gb-btn gb-btn--secondary"
            onClick={() => dispatch(fetchApiAccess())}
          >
            Reload
          </button>
        </>
      );
  }

  return (
    <div className="api-access">
      <div className="d-flex align-items-center mb-3">
        <ApiStatusBadge status={status} />
      </div>

      {status === "active" && data?.provisionedAt && (
        <p className="text-muted mb-3">
          Created {formatDate(data.provisionedAt)}
        </p>
      )}

      {body}

      {/* Rendered here rather than in Setting.js so the revealed
                credentials never travel further than this component. */}
      <ApiKeyReveal
        isOpen={!!revealed}
        credentials={revealed}
        mode={revealMode}
        prevExpiresAt={prevExpiresAt}
        onAcknowledge={handleAcknowledgeReveal}
      />
      <ConfirmRotateApiKey
        isOpen={confirmRotate}
        toggle={() => setConfirmRotate(!confirmRotate)}
        onConfirm={handleRotate}
        saving={saving}
      />
    </div>
  );
};

export default ApiAccess;
